import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useConsumptionSummary } from '@/api/hooks/useConsumption';
import DetectiveChips from '@/components/ai/DetectiveChips';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertTriangle, Droplets, Gauge } from 'lucide-react';
import type { ConsumptionVehicle } from '@/types/consumption';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const FLOAT = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 });

function driftColor(deltaPct: number | null): string {
  if (deltaPct == null) return 'hsl(215 16% 47%)';
  if (deltaPct >= 30) return 'hsl(0 84% 60%)';
  if (deltaPct >= 15) return 'hsl(24 95% 53%)';
  if (deltaPct >= 0) return 'hsl(48 96% 53%)';
  return 'hsl(142 71% 45%)';
}

function driftBadge(deltaPct: number | null): string {
  if (deltaPct == null) return 'bg-slate-100 text-slate-700 border-slate-200';
  if (deltaPct >= 30) return 'bg-red-100 text-red-800 border-red-200';
  if (deltaPct >= 15) return 'bg-orange-100 text-orange-800 border-orange-200';
  if (deltaPct >= 0) return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
}

export default function Consumption() {
  const { data, isLoading, error } = useConsumptionSummary();

  const chartData = (data?.worst_offenders ?? []).slice(0, 20).map((v) => ({
    label: v.registration ?? v.asset_token.slice(0, 8),
    actual: Number(v.actual_l_per_100km.toFixed(2)),
    expected: v.expected_l_per_100km,
    delta_pct: v.delta_pct,
  }));

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Consumption</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fuel efficiency (L/100km) — fleet baseline, drift vs expected, worst offenders.
        </p>
      </div>

      <DetectiveChips module="consumption" />

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-800">
            Could not load consumption data: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          title="Fleet avg L/100km"
          icon={<Gauge className="h-4 w-4 text-muted-foreground" />}
          value={
            data?.kpis.fleet_avg_l_per_100km != null
              ? `${FLOAT.format(data.kpis.fleet_avg_l_per_100km)} L/100km`
              : data
                ? '—'
                : null
          }
          loading={isLoading}
          subtle="last 90 days"
        />
        <Kpi
          title="Vehicles measured"
          icon={<Gauge className="h-4 w-4 text-muted-foreground" />}
          value={data ? NUM.format(data.kpis.vehicles_measured) : null}
          loading={isLoading}
          subtle="≥3 valid txn pairs"
        />
        <Kpi
          title="Vehicles drifting"
          icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
          value={data ? NUM.format(data.kpis.vehicles_drifting) : null}
          loading={isLoading}
          subtle="actual > 1.3× expected"
          accent={data && data.kpis.vehicles_drifting > 0 ? 'warn' : undefined}
        />
        <Kpi
          title="Total litres"
          icon={<Droplets className="h-4 w-4 text-muted-foreground" />}
          value={data ? `${NUM.format(data.kpis.total_litres)} L` : null}
          loading={isLoading}
          subtle="last 90 days"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Top 20 worst-performing assets — actual L/100km</CardTitle>
        </CardHeader>
        <CardContent className="h-72 px-2 py-2">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : chartData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No assets with measurable consumption in the last 90 days.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 4, right: 16, bottom: 40, left: 0 }}
              >
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  width={50}
                  tickFormatter={(v: number) => `${v}`}
                />
                <Tooltip
                  formatter={(v: number) => `${FLOAT.format(v)} L/100km`}
                  contentStyle={{
                    background: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="actual" radius={[2, 2, 0, 0]}>
                  {chartData.map((row, idx) => (
                    <Cell key={idx} fill={driftColor(row.delta_pct)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Drift table — top 20 offenders vs expected L/100km</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0">
          {isLoading ? (
            <div className="space-y-2 px-6 pb-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Registration</TableHead>
                  <TableHead>Make / Model</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead>Drift</TableHead>
                  <TableHead className="text-right">Txn pairs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.worst_offenders ?? []).map((v: ConsumptionVehicle) => (
                  <TableRow key={v.asset_token}>
                    <TableCell className="font-mono text-xs">
                      <Link
                        to={`/vehicles/${v.asset_token}`}
                        className="text-foreground hover:underline"
                      >
                        {v.registration ?? '—'}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[v.make, v.model].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {v.expected_l_per_100km != null
                        ? FLOAT.format(v.expected_l_per_100km)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {FLOAT.format(v.actual_l_per_100km)}
                    </TableCell>
                    <TableCell>
                      {v.delta_pct != null ? (
                        <Badge variant="outline" className={driftBadge(v.delta_pct)}>
                          {v.delta_pct >= 0 ? '+' : ''}
                          {v.delta_pct.toFixed(1)}%
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">no baseline</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                      {NUM.format(v.txn_count)}
                    </TableCell>
                  </TableRow>
                ))}
                {data && data.worst_offenders.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-sm text-muted-foreground"
                    >
                      No drift detected in the last 90 days.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface KpiProps {
  title: string;
  value: string | null;
  icon: React.ReactNode;
  loading: boolean;
  subtle?: string;
  accent?: 'warn';
}

function Kpi({ title, value, icon, loading, subtle, accent }: KpiProps) {
  return (
    <Card className={accent === 'warn' ? 'border-amber-200 bg-amber-50/40' : ''}>
      <CardHeader className="flex flex-row items-center justify-between pb-1.5 pt-3">
        <CardTitle className="text-xs font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {icon}
      </CardHeader>
      <CardContent className="pb-3 pt-0">
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <div className="text-2xl font-semibold tabular-nums">{value ?? '—'}</div>
        )}
        {subtle && (
          <div className="mt-1 text-xs text-muted-foreground">{subtle}</div>
        )}
      </CardContent>
    </Card>
  );
}
